/* global setTimeout, clearTimeout */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    ActivityIndicator,
    Modal,
    Platform,
    ScrollView,
    StyleSheet,
    Switch,
    Text,
    TextInput,
    TouchableOpacity,
    View,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { useTranslation } from 'react-i18next';
import { useWorkSheets, apiFetch } from '../helpers/WorkSheetContext';
import { ownerOfVehicle, searchServiceCustomers, searchServiceVehicles } from '../helpers/serviceLookupApi';
import VehicleSpecSelector from './VehicleSpecSelector';
import { PillTabs } from './premium';
import { ModalKeyboardAvoider } from './KeyboardAware';
import { useServiceMode } from '../helpers/ServiceModeContext';
import { SERVICE_REQUIREMENTS as DEFAULT_SERVICE_REQUIREMENTS } from '../helpers/serviceMode';

// Re-exported for back-compat — the canonical list now lives in
// `app/helpers/serviceMode.js` (DEFAULT_CATALOGS.serviceRequirements) so it
// can be overridden by an applied service mode.
const SERVICE_REQUIREMENTS = DEFAULT_SERVICE_REQUIREMENTS;
export { SERVICE_REQUIREMENTS };

const PRIORITY_OPTIONS = ['Low', 'Normal', 'High', 'Urgent'];

const MODAL_BRAND = {
    primary: '#F97316',
    primarySoft: 'rgba(249, 115, 22, 0.12)',
    border: '#ECEEF4',
    text: '#0F172A',
    textSecondary: '#64748B',
    muted: '#94A3B8',
    surface: '#FFFFFF',
    surfaceAlt: '#F3F4F8',
    success: '#16A34A',
    danger: '#DC2626',
};

const SUGGESTION_MAX_HEIGHT = 220;
const IS_WEB = Platform.OS === 'web';

// Picker lookups only hit the server from this many characters on, after this pause.
const LOOKUP_MIN_CHARS = 2;
const LOOKUP_DEBOUNCE_MS = 300;

const emptyForm = () => ({
    customerId: '', customerName: '', customerPhone: '', customerEmail: '',
    isCompany: false, taxNumber: '', companyName: '',
    vehicleId: '', vehicleBrand: '', vehicleModel: '', vehicleType: '',
    vehicleYear: '', fuel: '', displacement: '', powerKw: '', powerHp: '',
    color: '', engineCode: '', firstRegistration: '', licensePlate: '', vin: '',
    kilometers: '', motExpiry: '',
    priority: 'Normal', description: '', finishDate: '', accessories: '',
    serviceRequirements: [],
});

/**
 * Copy a (normalised) vehicle's fields onto the work-order form.
 *
 * @param {object} prev Current form state.
 * @param {object} vehicle Vehicle row (flat fields and/or `attributes`).
 * @returns {object} The form with the vehicle's identity + technical fields applied.
 */
const applyVehicleFields = (prev, vehicle) => ({
    ...prev,
    vehicleId: vehicle._id || '',
    vehicleBrand: vehicle.brand || vehicle.make || '',
    vehicleModel: vehicle.model || '',
    vehicleType: vehicle.type || vehicle.attributes?.type || '',
    vehicleYear: String(vehicle.year || vehicle.attributes?.year || ''),
    fuel: vehicle.fuel || vehicle.attributes?.fuel || '',
    displacement: String(vehicle.displacement || vehicle.attributes?.displacement || ''),
    powerKw: String(vehicle.powerKw || vehicle.attributes?.powerKw || ''),
    powerHp: String(vehicle.powerHp || vehicle.attributes?.powerHp || ''),
    color: vehicle.color || vehicle.attributes?.color || '',
    engineCode: vehicle.engineCode || vehicle.attributes?.engineCode || '',
    licensePlate: (vehicle.plate || vehicle.plateNumber || '').toUpperCase(),
    vin: (vehicle.vin || '').toUpperCase(),
    kilometers: String(vehicle.mileage || vehicle.kilometers || ''),
    firstRegistration: vehicle.firstRegistration || vehicle.attributes?.firstRegistration || '',
    motExpiry: vehicle.motExpiry || vehicle.attributes?.motExpiry || '',
});

const SectionIntro = ({ icon, title, subtitle, theme, accentColor = MODAL_BRAND.primary, action = null }) => (
    <View style={styles.sectionIntroRow}>
        <View style={styles.sectionIntroLeft}>
            <View style={[styles.sectionIntroIcon, { backgroundColor: `${accentColor}18` }]}>
                <Icon name={icon} size={18} color={accentColor} />
            </View>
            <View style={{ flex: 1 }}>
                <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>{title}</Text>
                {!!subtitle && <Text style={[styles.sectionSubtitle, { color: theme.colors.subText }]}>{subtitle}</Text>}
            </View>
        </View>
        {action}
    </View>
);

const MetaBadge = ({ icon, label, value, tone = MODAL_BRAND.primary, theme }) => (
    <View style={[styles.metaBadge, { backgroundColor: `${tone}14`, borderColor: `${tone}26` }]}>
        <Icon name={icon} size={14} color={tone} />
        <Text style={[styles.metaBadgeLabel, { color: theme.colors.subText }]}>{label}</Text>
        <Text style={[styles.metaBadgeValue, { color: theme.colors.text }]} numberOfLines={1}>{value || '—'}</Text>
    </View>
);

const FieldBlock = ({ label, children, theme }) => (
    <View style={styles.fieldBlock}>
        <Text style={[styles.fieldBlockLabel, { color: theme.colors.subText }]}>{label}</Text>
        {children}
    </View>
);

export default function NewWorkSheetModal({ visible, onClose, onSave, theme }) {
    const { t } = useTranslation();
    const { createCompositeWorksheet } = useWorkSheets();
    const { isFeatureEnabled, isFieldVisible, getCatalog, brands, addBrandLocal } = useServiceMode();
    const vehiclesEnabled = isFeatureEnabled('vehicles');
    const serviceRequirements = getCatalog('serviceRequirements', DEFAULT_SERVICE_REQUIREMENTS);
    const vehicleFieldVisible = useCallback((key) => isFieldVisible('vehicle', key, 'form'), [isFieldVisible]);
    const tr = useCallback((key, fallback) => {
        const translated = t(key);
        return translated && translated !== key ? translated : fallback;
    }, [t]);
    const [activeTab, setActiveTab] = useState('customer');
    const [form, setForm] = useState(emptyForm());
    const [customerQuery, setCustomerQuery] = useState('');
    const [vehicleQuery, setVehicleQuery] = useState('');
    const [companySuggestions, setCompanySuggestions] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    // Solid sheet surfaces straight from the (premium) theme: white cards on the airy canvas, navy in dark mode.
    const surfaceColor = theme.colors.card;
    const canvasColor = theme.colors.background;
    const shellBorder = theme.colors.border;
    const softSurface = theme.colors.inputBackground;

    const tabs = useMemo(() => ([
        { key: 'customer', label: tr('repair_customer_info', 'Ugyfel adatai'), icon: 'account' },
        ...(vehiclesEnabled ? [{ key: 'vehicle', label: tr('repair_vehicle_info', 'Jarmu adatai'), icon: 'car' }] : []),
        { key: 'service',  label: tr('repair_work', 'Munka'), icon: 'wrench' },
    ]), [tr, vehiclesEnabled]);

    const set = useCallback((key, value) => setForm(prev => ({ ...prev, [key]: value })), []);

    const priorityLabel = useCallback((p) => tr(`repair_priority_${String(p).toLowerCase()}`, p), [tr]);

    // Customer / vehicle suggestions are searched on the SERVER (debounced, stale
    // responses dropped) instead of scanning a local copy of every customer.
    const [existingCustomerSuggestions, setExistingCustomerSuggestions] = useState([]);
    useEffect(() => {
        const q = customerQuery.trim();
        if (!visible || q.length < LOOKUP_MIN_CHARS || form.customerId || form.isCompany) {
            setExistingCustomerSuggestions([]);
            return undefined;
        }
        let active = true;
        const handle = setTimeout(async () => {
            try {
                const { items } = await searchServiceCustomers(q, { limit: 6 });
                if (active) setExistingCustomerSuggestions(items);
            } catch {
                if (active) setExistingCustomerSuggestions([]);
            }
        }, LOOKUP_DEBOUNCE_MS);
        return () => {
            active = false;
            clearTimeout(handle);
        };
    }, [visible, customerQuery, form.customerId, form.isCompany]);

    const [vehicleSuggestions, setVehicleSuggestions] = useState([]);
    useEffect(() => {
        const q = vehicleQuery.trim();
        if (!visible || q.length < LOOKUP_MIN_CHARS) {
            setVehicleSuggestions([]);
            return undefined;
        }
        let active = true;
        const handle = setTimeout(async () => {
            try {
                const { items } = await searchServiceVehicles({ search: q, customerId: form.customerId || undefined, limit: 12 });
                if (active) setVehicleSuggestions(items.map((vehicle) => ({ customer: ownerOfVehicle(vehicle), vehicle })));
            } catch {
                if (active) setVehicleSuggestions([]);
            }
        }, LOOKUP_DEBOUNCE_MS);
        return () => {
            active = false;
            clearTimeout(handle);
        };
    }, [visible, vehicleQuery, form.customerId]);

    // The chosen customer's own vehicles (chips on the vehicle tab). Auto-selects the
    // first one when the customer was just picked from the suggestions, like the old
    // embedded `vehicles[0]` behaviour.
    const [customerVehicles, setCustomerVehicles] = useState([]);
    const autoPickVehicleForRef = useRef(null);
    useEffect(() => {
        if (!visible || !form.customerId) {
            setCustomerVehicles([]);
            return undefined;
        }
        let active = true;
        searchServiceVehicles({ customerId: form.customerId, limit: 20 })
            .then(({ items }) => {
                if (!active) return;
                setCustomerVehicles(items);
                if (autoPickVehicleForRef.current === form.customerId && items.length > 0) {
                    autoPickVehicleForRef.current = null;
                    setForm((prev) => (prev.customerId === form.customerId && !prev.vehicleId && !prev.licensePlate
                        ? applyVehicleFields(prev, items[0])
                        : prev));
                }
            })
            .catch(() => { if (active) setCustomerVehicles([]); });
        return () => { active = false; };
    }, [visible, form.customerId]);

    const applyVehicleSelection = useCallback((owner, vehicle) => {
        if (!vehicle) return;
        setForm((prev) => ({
            ...applyVehicleFields(prev, vehicle),
            customerId: prev.customerId || owner?._id || '',
            customerName: prev.customerName || owner?.name || '',
            customerPhone: prev.customerPhone || owner?.phone || '',
            customerEmail: prev.customerEmail || owner?.email || '',
        }));
        setVehicleQuery('');
    }, []);

    const handleSelectExistingCustomer = (c) => {
        const v = (c.vehicles || [])[0];
        autoPickVehicleForRef.current = c._id || null;
        setForm(prev => ({
            ...prev,
            customerId: c._id || '',
            customerName: c.name || '',
            customerPhone: c.phone || '',
            customerEmail: c.email || '',
            taxNumber: c.taxId || c.taxNumber || '',
            companyName: c.companyName || '',
            isCompany: !!c.isCompany,
            vehicleId: v?._id || '',
            vehicleBrand: v?.brand || v?.make || '',
            vehicleModel: v?.model || '',
            licensePlate: v?.plate || v?.plateNumber || '',
            vin: v?.vin || '',
            vehicleYear: String(v?.year || v?.attributes?.year || ''),
            kilometers: String(v?.mileage || ''),
            color: v?.color || v?.attributes?.color || '',
            fuel: v?.fuel || v?.attributes?.fuel || '',
            engineCode: v?.engineCode || v?.attributes?.engineCode || '',
        }));
        setCustomerQuery(c.name || '');
    };

    const handleClearCustomer = () => {
        setCustomerQuery('');
        setForm(emptyForm());
    };

    // Company-registry lookup: debounced, and a slow answer to an older keystroke is dropped.
    const companySearchRef = useRef({ timer: null, id: 0 });
    useEffect(() => () => clearTimeout(companySearchRef.current.timer), []);
    const handleCompanySearch = (text) => {
        set('customerName', text);
        const state = companySearchRef.current;
        clearTimeout(state.timer);
        const id = ++state.id;
        if (text.length < 2) { setCompanySuggestions([]); return; }
        state.timer = setTimeout(async () => {
            try {
                const resp = await fetch(`https://api.happysolutions.hu/api/restaurant/name/${encodeURIComponent(text)}/search`);
                const data = await resp.json();
                if (id === state.id) setCompanySuggestions(data?.suggestions || []);
            } catch { if (id === state.id) setCompanySuggestions([]); }
        }, 350);
    };

    const handleSelectCompany = (s) => {
        setForm(prev => ({
            ...prev,
            customerName: s.nev || s.name || '',
            companyName: s.nev || s.name || '',
            taxNumber: s.adoszam || s.taxId || '',
        }));
        setCompanySuggestions([]);
    };

    const toggleServiceReq = (value) => {
        setForm(prev => ({
            ...prev,
            serviceRequirements: (prev.serviceRequirements || []).includes(value)
                ? (prev.serviceRequirements || []).filter(v => v !== value)
                : [...(prev.serviceRequirements || []), value],
        }));
    };

    const handleSave = async () => {
        const customerName = (form.customerName || '').trim();
        if (!customerName && !form.customerId) return;
        if (!form.licensePlate.trim() && !form.vehicleId && !form.vehicleBrand) return;

        setSubmitting(true);
        try {
            const needsNewCustomer = !form.customerId && !!customerName;
            const needsNewVehicle = !form.vehicleId && !!(
                form.licensePlate || form.vehicleBrand || form.vehicleModel || form.vin
            );

            const buildCustomerPayload = () => ({
                name: { first: customerName.split(' ').slice(1).join(' ') || '-', last: customerName.split(' ')[0] || customerName },
                fullName: customerName,
                email: form.customerEmail,
                phone: form.customerPhone,
                mobile: form.customerPhone,
                isCompany: form.isCompany,
                taxId: form.taxNumber,
                companyName: form.isCompany ? (form.companyName || customerName) : undefined,
                isActive: true,
            });

            const buildVehiclePayload = () => ({
                plateNumber: form.licensePlate,
                vin: form.vin,
                make: form.vehicleBrand,
                brand: form.vehicleBrand,
                model: form.vehicleModel,
                mileage: form.kilometers ? parseInt(form.kilometers, 10) : 0,
                isActive: true,
                attributes: {
                    type: form.vehicleType,
                    year: form.vehicleYear,
                    fuel: form.fuel,
                    engineCode: form.engineCode,
                    displacement: form.displacement,
                    powerKw: form.powerKw,
                    powerHp: form.powerHp,
                    color: form.color,
                    firstRegistration: form.firstRegistration,
                    plateNumber: form.licensePlate,
                    vin: form.vin,
                },
            });

            // No client-side number: the backend assigns the next WO/<year>/<seq> from its
            // atomic counter (the old read-all-work-orders-then-max+1 could race and
            // downloaded the entire list on every save).
            const buildWorkOrderPayload = () => ({
                status: 'open',
                description: form.description,
                notes: form.description,
                vin: form.vin,
                kilometers: form.kilometers ? parseFloat(form.kilometers) : 0,
                licensePlate: form.licensePlate,
                priority: (form.priority || 'Normal').toLowerCase(),
                finishDate: form.finishDate ? new Date(form.finishDate).toISOString() : null,
                engineCode: form.engineCode,
                firstRegistration: form.firstRegistration,
                accessories: form.accessories,
                // Technical data of the chosen vehicle: a work order has no such fields, the backend fills them
                // onto the vehicle (a blank value never overwrites what the vehicle already has).
                fuel: form.fuel,
                displacement: form.displacement,
                powerKw: form.powerKw,
                powerHp: form.powerHp,
                customerId: form.customerId || undefined,
                vehicleId: form.vehicleId || undefined,
                attributes: {
                    serviceRequirements: form.serviceRequirements,
                    motExpiry: form.motExpiry,
                    brand: form.vehicleBrand,
                    model: form.vehicleModel,
                    type: form.vehicleType,
                    year: form.vehicleYear,
                    color: form.color,
                    vin: form.vin,
                    plateNumber: form.licensePlate,
                },
            });

            let result;
            if (needsNewCustomer || needsNewVehicle) {
                const compositePayload = { workOrder: buildWorkOrderPayload() };
                if (needsNewCustomer) compositePayload.customer = buildCustomerPayload();
                if (needsNewVehicle) compositePayload.vehicle = buildVehiclePayload();
                result = await createCompositeWorksheet(compositePayload);
            } else {
                result = await apiFetch('/serviceModule/work-orders', {
                    method: 'POST',
                    body: JSON.stringify(buildWorkOrderPayload()),
                });
            }

            const wo = result?.normalized || result?.workOrder || result;
            onSave && onSave(wo);
            setForm(emptyForm());
            setCustomerQuery('');
            setActiveTab('customer');
            onClose();
        } catch (err) {
            console.warn('[NewWorkSheetModal] save failed', err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const inp = [styles.input, { backgroundColor: softSurface, color: theme.colors.text, borderColor: shellBorder }];
    const half = [styles.input, styles.half, { backgroundColor: softSurface, color: theme.colors.text, borderColor: shellBorder }];

    const renderCustomerTab = () => (
        <View style={[styles.card, { backgroundColor: surfaceColor, borderColor: shellBorder }]}>
            <SectionIntro
                icon="account-circle-outline"
                    title={tr('repair_customer_info', 'Ugyfel adatai')}
                    subtitle={tr('repair_select_customer_help', 'Valassz meglevo ugyfelet vagy hozz letre ujat a munkalaphoz.')}
                action={!form.customerId ? (
                    <View style={styles.switchRow}>
                        <Text style={{ color: theme.colors.subText, fontSize: 12, fontWeight: '700' }}>{tr('repair_company', 'Ceg')}</Text>
                        <Switch value={form.isCompany} onValueChange={v => set('isCompany', v)} thumbColor={form.isCompany ? MODAL_BRAND.primary : '#f4f3f4'} trackColor={{ true: `${MODAL_BRAND.primary}80` }} />
                    </View>
                ) : null}
                theme={theme}
            />

            <View style={[styles.metricsRow, { marginBottom: 14 }]}>
                <MetaBadge icon="account" label={tr('repair_customer', 'Ugyfel')} value={form.customerName || tr('repair_not_selected', 'Nincs kivalasztva')} theme={theme} />
                <MetaBadge icon="phone-outline" label={tr('repair_phone', 'Telefon')} value={form.customerPhone} tone={MODAL_BRAND.success} theme={theme} />
            </View>

            {form.customerId ? (
                <View style={[styles.selectedBox, { backgroundColor: MODAL_BRAND.primarySoft, borderColor: `${MODAL_BRAND.primary}35` }]}>
                    <View style={{ flex: 1 }}>
                        <Text style={[styles.selectedName, { color: MODAL_BRAND.text }]}>{form.customerName}</Text>
                        <Text style={{ color: theme.colors.subText, fontSize: 12 }}>{[form.customerPhone, form.customerEmail].filter(Boolean).join(' · ') || '—'}</Text>
                    </View>
                    <TouchableOpacity onPress={handleClearCustomer} style={[styles.iconBtn, { backgroundColor: `${MODAL_BRAND.danger}15` }]}>
                        <Icon name="close" size={18} color={MODAL_BRAND.danger} />
                    </TouchableOpacity>
                </View>
            ) : (
                <>
                    <View style={[styles.innerPanel, { backgroundColor: softSurface, borderColor: shellBorder }]}>
                        <FieldBlock label={form.isCompany ? tr('repair_company_search', 'Ceg keresese') : tr('repair_search_customer_vehicle', 'Kereses ugyfelre')} theme={theme}>
                            <View
                                style={[
                                    styles.dropdownAnchor,
                                    (existingCustomerSuggestions.length > 0 || (companySuggestions.length > 0 && form.isCompany)) && styles.dropdownAnchorActive,
                                ]}
                            >
                                <TextInput
                                    style={inp}
                                    placeholder={tr('repair_search_customer_vehicle', 'Kereses (nev / telefon / email)')}
                                    placeholderTextColor={theme.colors.placeholder}
                                    value={form.isCompany ? form.customerName : (customerQuery || form.customerName)}
                                    onChangeText={v => {
                                        if (form.isCompany) { handleCompanySearch(v); }
                                        else { setCustomerQuery(v); set('customerName', v); }
                                    }}
                                />
                                {existingCustomerSuggestions.length > 0 && !form.isCompany && (
                                    <View style={[styles.dropdownMenu, { backgroundColor: surfaceColor, borderColor: shellBorder }]}>
                                        {existingCustomerSuggestions.map(c => (
                                            <TouchableOpacity key={c._id} style={[styles.suggestionItem, { borderBottomColor: shellBorder }]} onPress={() => handleSelectExistingCustomer(c)}>
                                                <Icon name="account-circle-outline" size={18} color={MODAL_BRAND.primary} style={{ marginRight: 8 }} />
                                                <View>
                                                    <Text style={{ color: theme.colors.text, fontWeight: '700' }}>{c.name}</Text>
                                                    <Text style={{ color: theme.colors.subText, fontSize: 11 }}>{c.phone || c.email || '-'} · {(c.vehicles || []).length} {tr('repair_vehicles_short', 'jarmu')}</Text>
                                                </View>
                                            </TouchableOpacity>
                                        ))}
                                    </View>
                                )}
                                {companySuggestions.length > 0 && form.isCompany && (
                                    <View style={[styles.dropdownMenu, { backgroundColor: surfaceColor, borderColor: shellBorder }]}>
                                        {companySuggestions.map((s, i) => (
                                            <TouchableOpacity key={i} style={[styles.suggestionItem, { borderBottomColor: shellBorder }]} onPress={() => handleSelectCompany(s)}>
                                                <Icon name="domain" size={18} color={MODAL_BRAND.primary} style={{ marginRight: 8 }} />
                                                <View>
                                                    <Text style={{ color: MODAL_BRAND.primary, fontWeight: '700' }}>{s.nev}</Text>
                                                    <Text style={{ color: theme.colors.subText, fontSize: 11 }}>{s.adoszam}</Text>
                                                </View>
                                            </TouchableOpacity>
                                        ))}
                                    </View>
                                )}
                            </View>
                        </FieldBlock>
                        <View style={styles.row}>
                            <FieldBlock label={tr('repair_phone', 'Telefon')} theme={theme}>
                                <TextInput style={half} placeholder={tr('repair_phone', 'Telefon')} placeholderTextColor={theme.colors.placeholder} value={form.customerPhone} keyboardType="phone-pad" onChangeText={v => set('customerPhone', v)} />
                            </FieldBlock>
                            <FieldBlock label={tr('repair_email', 'Email')} theme={theme}>
                                <TextInput style={half} placeholder={tr('repair_email', 'Email')} placeholderTextColor={theme.colors.placeholder} value={form.customerEmail} keyboardType="email-address" onChangeText={v => set('customerEmail', v)} />
                            </FieldBlock>
                        </View>
                    </View>
                    {form.isCompany && (
                        <View style={[styles.innerPanel, { backgroundColor: softSurface, borderColor: shellBorder }]}>
                            <SectionIntro
                                icon="domain"
                                title={tr('repair_company', 'Ceges adatok')}
                                subtitle={tr('repair_company_summary', 'A cegadatokat automatikusan atemeljuk az ugyfel rekordba.')}
                                theme={theme}
                                accentColor={MODAL_BRAND.success}
                            />
                            <View style={styles.row}>
                                <FieldBlock label={tr('repair_company_name', 'Cegnev')} theme={theme}>
                                    <TextInput style={half} placeholder={tr('repair_company_name', 'Cegnev')} placeholderTextColor={theme.colors.placeholder} value={form.companyName} onChangeText={v => set('companyName', v)} />
                                </FieldBlock>
                                <FieldBlock label={tr('repair_tax_number', 'Adoszam')} theme={theme}>
                                    <TextInput style={half} placeholder={tr('repair_tax_number', 'Adoszam')} placeholderTextColor={theme.colors.placeholder} value={form.taxNumber} onChangeText={v => set('taxNumber', v)} />
                                </FieldBlock>
                            </View>
                        </View>
                    )}
                </>
            )}
        </View>
    );

    const renderVehicleTab = () => {
        return (
            <View style={[styles.card, { backgroundColor: surfaceColor, borderColor: shellBorder }]}>
                <SectionIntro
                    icon="car-info"
                    title={tr('repair_vehicle_info', 'Jarmu adatai')}
                    subtitle={tr('repair_vehicle_details_hint', 'Az admin felulet szerinti azonositok es muszaki adatok egy helyen.')}
                    theme={theme}
                    accentColor={MODAL_BRAND.success}
                />

                <View style={[styles.metricsRow, { marginBottom: 14 }]}>
                    <MetaBadge icon="card-account-details-outline" label={tr('repair_plate_number', 'Rendszam')} value={form.licensePlate} tone={MODAL_BRAND.success} theme={theme} />
                    <MetaBadge icon="identifier" label="VIN" value={form.vin} theme={theme} />
                </View>

                <FieldBlock label={tr('repair_search_vehicle', 'Meglevo jarmu keresese')} theme={theme}>
                    <View style={[styles.dropdownAnchor, vehicleSuggestions.length > 0 && styles.dropdownAnchorActive]}>
                        <TextInput
                            style={inp}
                            placeholder={tr('repair_search_customer_vehicle', 'Kereses ugyfelre, rendszamra, alvazszamra')}
                            placeholderTextColor={theme.colors.placeholder}
                            value={vehicleQuery}
                            onChangeText={setVehicleQuery}
                        />
                        {vehicleSuggestions.length > 0 && (
                            <View style={[styles.dropdownMenu, { backgroundColor: surfaceColor, borderColor: shellBorder }]}>
                                {vehicleSuggestions.map(({ customer, vehicle }) => (
                                    <TouchableOpacity
                                        key={`${customer?._id || 'c'}-${vehicle?._id || vehicle?.plate || vehicle?.vin}`}
                                        style={[styles.suggestionItem, { borderBottomColor: shellBorder }]}
                                        onPress={() => applyVehicleSelection(customer, vehicle)}
                                    >
                                        <Icon name="car" size={16} color={MODAL_BRAND.primary} style={{ marginRight: 8 }} />
                                        <View style={{ flex: 1 }}>
                                            <Text style={{ color: theme.colors.text, fontWeight: '700' }}>
                                                {[vehicle.brand || vehicle.make, vehicle.model].filter(Boolean).join(' ')}
                                            </Text>
                                            <Text style={{ color: theme.colors.subText, fontSize: 11 }}>
                                                {(vehicle.plate || vehicle.plateNumber || '').toUpperCase()} · {(vehicle.vin || '').toUpperCase()} · {customer?.name || '—'}
                                            </Text>
                                        </View>
                                    </TouchableOpacity>
                                ))}
                            </View>
                        )}
                    </View>
                </FieldBlock>
                {customerVehicles.length > 0 && (
                    <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 12 }}>
                        {customerVehicles.map(v => {
                            const isActive = form.vehicleId === v._id;
                            return (
                                <TouchableOpacity
                                    key={v._id || v.plate}
                                    style={[styles.vehicleChip, { borderColor: isActive ? MODAL_BRAND.primary : shellBorder, backgroundColor: isActive ? MODAL_BRAND.primarySoft : softSurface }]}
                                    onPress={() => setForm(prev => ({
                                        ...prev, vehicleId: v._id || '',
                                        vehicleBrand: v.brand || v.make || '', vehicleModel: v.model || '',
                                        licensePlate: v.plate || v.plateNumber || '', vin: v.vin || '',
                                        vehicleYear: String(v.year || v.attributes?.year || ''),
                                        kilometers: String(v.mileage || ''), color: v.color || v.attributes?.color || '',
                                        fuel: v.fuel || v.attributes?.fuel || '', engineCode: v.engineCode || v.attributes?.engineCode || '',
                                    }))}
                                >
                                    <Icon name="car" size={14} color={isActive ? MODAL_BRAND.primary : theme.colors.subText} style={{ marginBottom: 2 }} />
                                    <Text style={{ color: isActive ? MODAL_BRAND.primary : theme.colors.text, fontWeight: '700', fontSize: 12 }}>{v.brand} {v.model}</Text>
                                    <Text style={{ color: theme.colors.subText, fontSize: 11, fontWeight: '700' }}>{v.plate || v.plateNumber}</Text>
                                </TouchableOpacity>
                            );
                        })}
                    </ScrollView>
                )}

                <View style={[styles.innerPanel, { backgroundColor: softSurface, borderColor: shellBorder }]}>
                    <VehicleSpecSelector
                        value={{
                            brand: form.vehicleBrand,
                            model: form.vehicleModel,
                            type: form.vehicleType,
                            year: form.vehicleYear,
                            fuel: form.fuel,
                            engineCode: form.engineCode,
                            displacement: form.displacement,
                            powerKw: form.powerKw,
                            powerHp: form.powerHp,
                            color: form.color,
                        }}
                        onChange={(nextVehicle) => {
                            setForm((prev) => ({
                                ...prev,
                                vehicleBrand: nextVehicle.brand,
                                vehicleModel: nextVehicle.model,
                                vehicleType: nextVehicle.type,
                                vehicleYear: nextVehicle.year,
                                fuel: nextVehicle.fuel,
                                engineCode: nextVehicle.engineCode,
                                displacement: nextVehicle.displacement,
                                powerKw: nextVehicle.powerKw,
                                powerHp: nextVehicle.powerHp,
                                color: nextVehicle.color,
                            }));
                        }}
                        theme={theme}
                        tr={tr}
                        brands={brands}
                        isFieldVisible={vehicleFieldVisible}
                        onAddBrand={(name) => {
                            if (!name) return;
                            addBrandLocal({ name });
                            set('vehicleBrand', name);
                        }}
                    />

                    <View style={styles.row}>
                        {vehicleFieldVisible('licensePlate') && (
                        <FieldBlock label={tr('repair_plate_number', 'Rendszam')} theme={theme}><TextInput style={half} placeholder={tr('repair_plate_number', 'Rendszam')} placeholderTextColor={theme.colors.placeholder} value={form.licensePlate} onChangeText={v => set('licensePlate', v.toUpperCase())} /></FieldBlock>
                        )}
                        {vehicleFieldVisible('vin') && (
                        <FieldBlock label="VIN" theme={theme}><TextInput style={half} placeholder="VIN" placeholderTextColor={theme.colors.placeholder} value={form.vin} onChangeText={v => set('vin', v.toUpperCase())} /></FieldBlock>
                        )}
                    </View>
                    <View style={styles.row}>
                        {vehicleFieldVisible('kilometers') && (
                        <FieldBlock label={tr('repair_mileage', 'Kilometerora (km)')} theme={theme}><TextInput style={half} placeholder={tr('repair_mileage', 'Kilometerora (km)')} placeholderTextColor={theme.colors.placeholder} value={form.kilometers} keyboardType="numeric" onChangeText={v => set('kilometers', v)} /></FieldBlock>
                        )}
                        {vehicleFieldVisible('firstRegistration') && (
                        <FieldBlock label={tr('repair_first_registration', 'Forgalomba helyezes')} theme={theme}><TextInput style={half} placeholder={tr('repair_first_registration', 'Forgalomba helyezes')} placeholderTextColor={theme.colors.placeholder} value={form.firstRegistration} onChangeText={v => set('firstRegistration', v)} /></FieldBlock>
                        )}
                    </View>
                    {vehicleFieldVisible('motExpiry') && (
                    <View style={styles.row}>
                        <FieldBlock label={tr('repair_mot_expiry', 'Muszaki vizsga lejarata')} theme={theme}><TextInput style={half} placeholder={tr('repair_mot_expiry', 'Muszaki vizsga lejarata')} placeholderTextColor={theme.colors.placeholder} value={form.motExpiry} onChangeText={v => set('motExpiry', v)} /></FieldBlock>
                    </View>
                    )}
                </View>
            </View>
        );
    };

    const renderServiceTab = () => (
        <View>
            <View style={[styles.card, { backgroundColor: surfaceColor, borderColor: shellBorder }]}>
                <SectionIntro
                    icon="clipboard-text-clock-outline"
                    title={tr('repair_work', 'Munka')}
                    subtitle={tr('repair_service_planning_hint', 'Allitsd be a prioritast, a hataridot es a munkaleirast az admin kepernyo ritmusaban.')}
                    theme={theme}
                    accentColor={MODAL_BRAND.primary}
                />
                <View style={[styles.metricsRow, { marginBottom: 14 }]}>
                    <MetaBadge icon="flag-outline" label={tr('repair_priority', 'Prioritas')} value={priorityLabel(form.priority)} tone={MODAL_BRAND.danger} theme={theme} />
                    <MetaBadge icon="calendar-clock" label={tr('repair_expected_finish', 'Hatarido')} value={form.finishDate} theme={theme} />
                </View>
                <View style={[styles.innerPanel, { backgroundColor: softSurface, borderColor: shellBorder }]}>
                    <View style={styles.row}>
                        <FieldBlock label={tr('repair_expected_finish', 'Varhato hatarido')} theme={theme}>
                            <TextInput style={half} placeholder={tr('repair_expected_finish', 'Varhato hatarido')} placeholderTextColor={theme.colors.placeholder} value={form.finishDate} onChangeText={v => set('finishDate', v)} />
                        </FieldBlock>
                        <FieldBlock label={tr('repair_priority', 'Prioritas')} theme={theme}>
                            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={[styles.half, { marginBottom: 8 }]}>
                                <View style={{ flexDirection: 'row', gap: 6 }}>
                                    {PRIORITY_OPTIONS.map(p => {
                                        const active = form.priority === p;
                                        return (
                                            <TouchableOpacity key={p} onPress={() => set('priority', p)} style={[styles.priorityChip, { borderColor: active ? MODAL_BRAND.primary : shellBorder, backgroundColor: active ? MODAL_BRAND.primarySoft : 'transparent' }]}>
                                                <Text style={{ fontSize: 12, color: active ? MODAL_BRAND.primary : theme.colors.subText, fontWeight: active ? '700' : '500' }}>{priorityLabel(p)}</Text>
                                            </TouchableOpacity>
                                        );
                                    })}
                                </View>
                            </ScrollView>
                        </FieldBlock>
                    </View>
                    <FieldBlock label={tr('repair_job_summary', 'Munkaleiras / megjegyzes')} theme={theme}>
                        <TextInput style={[inp, styles.textArea]} placeholder={tr('repair_job_summary', 'Munkaleiras / megjegyzes')} placeholderTextColor={theme.colors.placeholder} multiline value={form.description} onChangeText={v => set('description', v)} />
                    </FieldBlock>
                    <FieldBlock label={tr('repair_accessories', 'Tartozekok')} theme={theme}>
                        <TextInput style={inp} placeholder={tr('repair_accessories', 'Tartozekok')} placeholderTextColor={theme.colors.placeholder} value={form.accessories} onChangeText={v => set('accessories', v)} />
                    </FieldBlock>
                </View>
            </View>

            <View style={[styles.card, { backgroundColor: surfaceColor, borderColor: shellBorder }]}>
                <SectionIntro
                    icon="format-list-checks"
                    title={tr('repair_service_requirements', 'Szukseges munkak')}
                    subtitle={tr('repair_service_requirements_hint', 'Jelold azokat a szakagi feladatokat, amelyek az admin nezetben is kiemelve latszanak.')}
                    theme={theme}
                    accentColor={MODAL_BRAND.success}
                />
                <View style={styles.reqGrid}>
                    {serviceRequirements.map(req => {
                        const active = (form.serviceRequirements || []).includes(req.value);
                        return (
                            <TouchableOpacity key={req.value} style={[styles.reqChip, { borderColor: active ? MODAL_BRAND.primary : shellBorder, backgroundColor: active ? MODAL_BRAND.primarySoft : softSurface }]} onPress={() => toggleServiceReq(req.value)}>
                                <Icon name={req.icon} size={20} color={active ? MODAL_BRAND.primary : theme.colors.subText} />
                                <Text style={{ fontSize: 11, color: active ? MODAL_BRAND.primary : theme.colors.text, fontWeight: active ? '700' : '500', marginTop: 4, textAlign: 'center' }}>
                                    {tr(req.labelKey, req.value)}
                                </Text>
                            </TouchableOpacity>
                        );
                    })}
                </View>
            </View>
        </View>
    );

    return (
        <Modal visible={visible} animationType="slide" transparent>
            <ModalKeyboardAvoider style={styles.overlay} justify="flex-end">
                <View style={[styles.container, { backgroundColor: canvasColor, borderColor: shellBorder }]}>
                    <View style={[styles.header, { borderBottomColor: shellBorder, backgroundColor: surfaceColor }]}>
                        <TouchableOpacity onPress={onClose} style={[styles.iconBtn, { backgroundColor: softSurface }]}>
                            <Icon name="close" size={22} color={theme.colors.text} />
                        </TouchableOpacity>
                        <View style={styles.headerCopy}>
                            <Text style={[styles.eyebrow, { color: theme.colors.subText }]}>{tr('repair_worksheet_center', 'MUNKALAP KOZPONT')}</Text>
                            <Text style={[styles.title, { color: theme.colors.text }]}>{tr('repair_new_worksheet', 'Uj munkalap')}</Text>
                            <Text style={[styles.titleSubtext, { color: theme.colors.subText }]}>
                                {tr('repair_new_worksheet_subtitle', 'Ugyfel, jarmu es szervizadatok egy admin stilusu szerkesztofeluleten.')}
                            </Text>
                        </View>
                        <TouchableOpacity onPress={handleSave} disabled={submitting} style={[styles.saveBtn, { backgroundColor: submitting ? theme.colors.border : MODAL_BRAND.primary }]}>
                            {submitting ? <ActivityIndicator size="small" color="#fff" /> : <><Icon name="content-save-outline" size={18} color="#fff" /><Text style={styles.saveBtnText}>{tr('repair_save', 'Mentes')}</Text></>}
                        </TouchableOpacity>
                    </View>

                    <View style={[styles.tabsRow, { backgroundColor: canvasColor }]}>
                        <PillTabs
                            variant="solid"
                            options={tabs.map(tab => ({ value: tab.key, label: tab.label, icon: tab.icon }))}
                            value={activeTab}
                            onChange={setActiveTab}
                        />
                    </View>

                    <ScrollView contentContainerStyle={[styles.content, { overflow: 'visible' }]} keyboardShouldPersistTaps="handled" style={{ backgroundColor: canvasColor, overflow: 'visible' }}>
                        {activeTab === 'customer' && renderCustomerTab()}
                        {activeTab === 'vehicle' && renderVehicleTab()}
                        {activeTab === 'service' && renderServiceTab()}
                        <View style={{ height: 80 }} />
                    </ScrollView>
                </View>
            </ModalKeyboardAvoider>
        </Modal>
    );
}

const styles = StyleSheet.create({
    overlay: { flex: 1, backgroundColor: 'rgba(2, 6, 23, 0.58)', justifyContent: 'flex-end' },
    container: {
        height: '93%',
        borderTopLeftRadius: 30,
        borderTopRightRadius: 30,
        overflow: 'visible',
        position: 'relative',
        zIndex: 1,
        borderWidth: 1,
        ...Platform.select({
            web: { boxShadow: '0 -18px 40px rgba(15, 23, 42, 0.18)' },
            ios: { shadowColor: '#0F172A', shadowOffset: { width: 0, height: -8 }, shadowOpacity: 0.12, shadowRadius: 16 },
            android: { elevation: 10 },
        }),
    },
    header: { flexDirection: 'row', alignItems: 'center', gap: 14, paddingHorizontal: 16, paddingVertical: 16, borderBottomWidth: 1 },
    headerCopy: { flex: 1 },
    eyebrow: { fontSize: 11, fontWeight: '800', letterSpacing: 1.1, textTransform: 'uppercase', marginBottom: 3 },
    title: { fontSize: 19, fontWeight: '800' },
    titleSubtext: { fontSize: 12, marginTop: 4, lineHeight: 18 },
    iconBtn: { width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center' },
    saveBtn: { flexDirection: 'row', gap: 8, paddingHorizontal: 16, paddingVertical: 11, borderRadius: 999, minWidth: 94, alignItems: 'center', justifyContent: 'center' },
    saveBtnText: { color: '#fff', fontWeight: '700', fontSize: 14 },
    tabsRow: { paddingHorizontal: 12, paddingTop: 10, paddingBottom: 4 },
    content: { padding: 16, overflow: 'visible', position: 'relative', zIndex: 1 },
    card: { borderRadius: 24, padding: 18, marginBottom: 16, borderWidth: 1, overflow: 'visible', position: 'relative', zIndex: 1 },
    cardHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 },
    switchRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
    cardTitle: { fontSize: 15, fontWeight: '700' },
    sectionIntroRow: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 14 },
    sectionIntroLeft: { flex: 1, flexDirection: 'row', alignItems: 'flex-start', gap: 12 },
    sectionIntroIcon: { width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center' },
    sectionTitle: { fontSize: 16, fontWeight: '800', marginBottom: 2 },
    sectionSubtitle: { fontSize: 12, lineHeight: 18 },
    metricsRow: { flexDirection: 'row', gap: 10 },
    metaBadge: { flex: 1, minHeight: 64, borderRadius: 20, borderWidth: 1, paddingHorizontal: 12, paddingVertical: 10, gap: 4 },
    metaBadgeLabel: { fontSize: 11, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.4 },
    metaBadgeValue: { fontSize: 13, fontWeight: '700' },
    innerPanel: { borderRadius: 20, borderWidth: 1, padding: 14, marginBottom: 12, overflow: 'visible', position: 'relative', zIndex: 1 },
    fieldBlock: { flex: 1, marginBottom: 8 },
    fieldBlockLabel: { fontSize: 11, fontWeight: '800', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4 },
    input: {
        borderRadius: 16,
        borderWidth: 1,
        paddingHorizontal: 12,
        height: 48,
        fontSize: 16,
        marginBottom: 0,
        ...(Platform.OS === 'web' ? { outlineWidth: 0, outlineColor: 'transparent', outlineStyle: 'none' } : {}),
    },
    half: { flex: 1 },
    textArea: { height: 96, textAlignVertical: 'top', paddingTop: 12 },
    row: { flexDirection: 'row', gap: 8, overflow: 'visible', position: 'relative', zIndex: 1 },
    dropdownAnchor: {
        position: 'relative',
        zIndex: 99999990,
        ...(IS_WEB ? { isolation: 'isolate' } : {}),
        overflow: 'visible',
    },
    dropdownAnchorActive: { zIndex: 99999995, elevation: 9998 },
    dropdownMenu: {
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        marginTop: 6,
        zIndex: 99999999,
        elevation: 9999,
        borderWidth: 1,
        borderRadius: 18,
        maxHeight: SUGGESTION_MAX_HEIGHT,
        overflow: 'hidden',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.12,
        shadowRadius: 10,
    },
    suggestionItem: { flexDirection: 'row', alignItems: 'center', padding: 12, borderBottomWidth: 1 },
    selectedBox: { flexDirection: 'row', alignItems: 'center', padding: 14, borderRadius: 16, borderWidth: 1 },
    selectedName: { fontSize: 15, fontWeight: '700', marginBottom: 2 },
    vehicleChip: { paddingHorizontal: 14, paddingVertical: 10, borderRadius: 14, borderWidth: 1, marginRight: 8, alignItems: 'center', minWidth: 100 },
    priorityChip: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 16, borderWidth: 1 },
    reqGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 10 },
    reqChip: { width: '30%', alignItems: 'center', paddingVertical: 16, paddingHorizontal: 8, borderRadius: 16, borderWidth: 1 },
});
